import { useCallback, useEffect, useRef, useState } from "react"
import { Editor as TiptapEditor } from "@tiptap/react"
import { WebsocketProvider } from "y-websocket"

import { CollaborationService } from "services/collaborationService"
import { htmlToPlainText, stripCommentSpans } from "utils/editorUtils"
import {
  EditorSaveOptions,
  QuestionnaireSaveOptions,
  Variant,
} from "types/questionnaire.types"

interface UseQuestionnaireSaveProps {
  /** The TipTap editor instance */
  editor: TiptapEditor | null
  /** Whether collaboration is enabled */
  collaborationEnabled: boolean
  /** Whether the collaboration WebSocket is connected */
  isConnected: boolean
  /** Whether the collaboration session is initialized */
  isCollaborationReady: boolean
  /** Current selected variant name */
  currentVariant: string
  /** The variants data */
  variants: Variant[]
  /** Full questionnaire text content */
  fullQuestionnaire: string
  /** Function to fetch the latest questionnaire from backend */
  fetchLatestQuestionnaire?: () => Promise<string>
  /** Function to save the questionnaire content */
  saveQuestionnaire: (
    updatedVariantsParam?: Variant[],
    collaborativeContent?: string,
    options?: EditorSaveOptions,
  ) => Promise<void>
  /** Converts editor content to the backend text format */
  convertToTextFormat: (
    currentContent: string,
    variants: Variant[],
    currentVariant?: string,
    fullQuestionnaire?: string,
  ) => string
  /** Returns the WebSocket provider for a variant */
  getProvider: (variantName: string) => WebsocketProvider | null
  /** The collaboration service instance */
  collaborationService: CollaborationService | null
  /** Whether the editor content has unsaved changes */
  contentChanged: boolean
  /** Setter for the unsaved-changes flag */
  setContentChanged: React.Dispatch<React.SetStateAction<boolean>>
  /** Setter for the non-collaborative editor content state */
  setEditorContent: React.Dispatch<React.SetStateAction<string>>
}

/**
 * A save of its own, run by runExclusiveSave. Before it changes the editor it calls
 * `keepCurrentText`, which saves edits not yet in a version as a checkpoint (and rejects
 * when that save fails); a save that decides to do nothing doesn't call it.
 */
export type ExclusiveSave = (keepCurrentText: () => Promise<void>) => Promise<void>

interface UseQuestionnaireSaveReturn {
  isSaving: boolean
  /** Saves the editor's questionnaire (`checkpoint` cuts a version). Resolves `true` when the save went through, `false` when it was skipped or failed */
  performSave: (options?: QuestionnaireSaveOptions) => Promise<boolean>
  /** A manual save (the Save button, Ctrl+S): a checkpoint. As performSave */
  manualSaveQuestionnaire: () => Promise<boolean>
  /** Runs a save of its own after the editor's saves in flight, holding them back meanwhile */
  runExclusiveSave: (save: ExclusiveSave) => Promise<void>
}

/**
 * useQuestionnaireSave owns the questionnaire save workflow: the shared
 * performSave function (collaborative and plain modes), the 10-second
 * autosave interval, and manual saving via the Ctrl+S shortcut. A manual
 * save is a checkpoint (it cuts a version in the project history), and so is
 * closing the editor (leaving the Questionnaire tab or the project) when there
 * are edits or autosaves since the last checkpoint; an autosave only saves.
 */
export const useQuestionnaireSave = ({
  editor,
  collaborationEnabled,
  isConnected,
  isCollaborationReady,
  currentVariant,
  variants,
  fullQuestionnaire,
  fetchLatestQuestionnaire,
  saveQuestionnaire,
  convertToTextFormat,
  getProvider,
  collaborationService,
  contentChanged,
  setContentChanged,
  setEditorContent,
}: UseQuestionnaireSaveProps): UseQuestionnaireSaveReturn => {
  const [isSaving, setIsSaving] = useState<boolean>(false)

  /**
   * Converts HTML content back to plain text (uses utility function)
   * Strips comment spans first to prevent them from breaking the backend parser
   */
  const htmlToFilteredText = useCallback((html: string): string => {
    const cleanedHtml = stripCommentSpans(html)
    return htmlToPlainText(cleanedHtml)
  }, [])

  // The editor's saves running now (autosave, manual), and how many saves of their own
  // (a restore) are holding the editor's saves back
  const savesInFlightRef = useRef<Set<Promise<boolean>>>(new Set())
  const heldBackRef = useRef<number>(0)
  // Clears the "saving" flag a second after a save; cancelled if the editor closes first
  const savingFlagTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(savingFlagTimerRef.current), [])
  // An autosave has saved text that no checkpoint has recorded as a version yet
  const uncheckpointedRef = useRef<boolean>(false)

  /**
   * Shared save function used by both autosave and manual save.
   * Fetches the latest questionnaire from the backend before saving
   * to ensure variant changes are properly preserved.
   *
   * Never rejects: resolves `true` when the questionnaire was saved, `false`
   * when there was nothing to save with (no editor), the save was held back
   * (collaborative and disconnected), or it failed (already reported).
   */
  const performSave = async (
    options: QuestionnaireSaveOptions = {},
  ): Promise<boolean> => {
    if (!editor) return false
    // Block saves when disconnected in collaborative mode — edits stay in
    // IndexedDB and will sync via Yjs when reconnected. This prevents
    // overwriting other users' changes with stale local content.
    if (collaborationEnabled && !isConnected) return false
    // A restore is saving the questionnaire itself: a save built from the editor
    // now could land after it and undo it. The next autosave catches up.
    if (heldBackRef.current > 0) return false
    const save = saveFromEditor(editor, { checkpoint: !!options.checkpoint })
    savesInFlightRef.current.add(save)
    try {
      return await save
    } finally {
      savesInFlightRef.current.delete(save)
    }
  }

  /**
   * Builds the questionnaire from the editor (and, in collaborative mode, the
   * other variants' Yjs docs) and saves it. Resolves `true` once saved; errors
   * are logged and resolve `false`, and are rejected only with `rethrow`.
   */
  const saveFromEditor = async (
    activeEditor: TiptapEditor,
    options: QuestionnaireSaveOptions,
    rethrow = false,
  ): Promise<boolean> => {
    setIsSaving(true)

    try {
      if (isCollaborationReady && collaborationEnabled) {
        // Wait for a fresh Yjs sync before reading editor state — guarantees
        // the editor reflects the latest server state (including other users'
        // edits) before we build the save payload. 2s timeout as safety net.
        const variantKey = currentVariant === "" ? "master" : currentVariant
        const provider = getProvider(variantKey)
        if (provider && provider.wsconnected) {
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(resolve, 2000)
            const onSync = (synced: boolean) => {
              if (synced) {
                clearTimeout(timeout)
                provider.off("sync", onSync)
                resolve()
              }
            }
            provider.on("sync", onSync)
          })
        }

        const latestQuestionnaire = fetchLatestQuestionnaire
          ? await fetchLatestQuestionnaire()
          : fullQuestionnaire
        const currentHTML = activeEditor.getHTML()
        const currentTextContent = htmlToFilteredText(currentHTML)
        const collaborativeText = convertToTextFormat(
          currentTextContent,
          variants,
          currentVariant,
          latestQuestionnaire,
        )
        await saveQuestionnaire(undefined, collaborativeText, options)
        // Record save timestamp for stale Yjs detection
        if (collaborationService) {
          const variantKey = currentVariant === "" ? "master" : currentVariant
          collaborationService.recordSaveTimestamp(variantKey)
        }
      } else {
        setEditorContent(activeEditor.getHTML())
        await saveQuestionnaire(undefined, undefined, options)
      }
      uncheckpointedRef.current = !options.checkpoint
      setContentChanged(false)
      savingFlagTimerRef.current = setTimeout(() => setIsSaving(false), 1000)
      return true
    } catch (error) {
      setIsSaving(false)
      if (rethrow) throw error
      console.error("Error saving questionnaire:", error)
      return false
    }
  }

  // What runExclusiveSave reads when it runs: it is created once, so it can't close over them
  const exclusiveRef = useRef({ editor, contentChanged, saveFromEditor })
  exclusiveRef.current = { editor, contentChanged, saveFromEditor }

  // Auto save the questionnaire every 10 seconds
  // In collaborative mode, only save when connected to prevent overwrites
  useEffect(() => {
    let saveInterval: NodeJS.Timeout | undefined

    const shouldSave = collaborationEnabled
      ? editor && isConnected && contentChanged // Only save when connected AND content changed
      : editor && contentChanged // Only save with changes in non-collaborative

    if (shouldSave) {
      saveInterval = setInterval(() => {
        performSave()
      }, 10000) // 10 seconds
    }

    return () => {
      if (saveInterval) {
        clearInterval(saveInterval)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editor,
    saveQuestionnaire,
    contentChanged,
    isCollaborationReady,
    collaborationEnabled,
    isConnected,
    htmlToFilteredText,
    convertToTextFormat,
    variants,
    currentVariant,
    fullQuestionnaire,
    fetchLatestQuestionnaire,
  ])

  /**
   * Manually saves the questionnaire content, as a checkpoint.
   * Delegates to the shared performSave function.
   */
  const manualSaveQuestionnaire = (): Promise<boolean> =>
    performSave({ checkpoint: true })

  /**
   * Runs a save of its own (a restore saving the version it loaded) once the
   * editor's saves already running are done, and holds the editor's saves back
   * until it finishes, so neither can land on top of the other. Its
   * `keepCurrentText`: when there are edits not saved yet, or autosaves no
   * checkpoint has recorded, the editor's text is saved as a checkpoint, so the
   * text the save replaces stays in the history. If that save fails, the error
   * is passed on and the save goes no further.
   */
  const runExclusiveSave = useCallback(
    async (save: ExclusiveSave): Promise<void> => {
      heldBackRef.current += 1
      try {
        await Promise.all(savesInFlightRef.current)
        let replacing = false
        const keepCurrentText = async (): Promise<void> => {
          replacing = true
          const latest = exclusiveRef.current
          if (latest.editor && (latest.contentChanged || uncheckpointedRef.current)) {
            await latest.saveFromEditor(latest.editor, { checkpoint: true }, true)
          }
        }
        await save(keepCurrentText)
        // The restore's save is a checkpoint: nothing is left that no version has recorded
        if (replacing) uncheckpointedRef.current = false
      } finally {
        heldBackRef.current -= 1
      }
    },
    []
  )

  /**
   * Keyboard shortcut handler for Save (Ctrl+S)
   */
  const manualSaveRef = useRef(manualSaveQuestionnaire)
  manualSaveRef.current = manualSaveQuestionnaire

  useEffect(() => {
    const handleCtrlS = (e: KeyboardEvent) => {
      if (e.ctrlKey && !e.shiftKey && e.key === "s") {
        e.preventDefault()
        manualSaveRef.current()
      }
    }

    window.addEventListener("keydown", handleCtrlS)
    return () => window.removeEventListener("keydown", handleCtrlS)
  }, [])

  /**
   * Closing the editor is a checkpoint: when there are edits not saved yet, or
   * autosaves no checkpoint has recorded, the editor's text is saved once more
   * as a checkpoint so the history gets the version. Runs on unmount. Everything
   * the save needs is taken there and then (the editor's HTML, the variants, the
   * open variant, the questionnaire), so the save never reads the editor or its
   * ref after they are gone. In collaborative mode the other variants still come
   * from their Yjs docs and the latest saved questionnaire, as in any save.
   */
  const closeRef = useRef<() => void>(() => {})
  closeRef.current = () => {
    if (!editor || !(contentChanged || uncheckpointedRef.current)) return
    if (collaborationEnabled && !isConnected) return
    if (heldBackRef.current > 0) return
    let html: string
    try {
      html = editor.getHTML()
    } catch (error) {
      console.error("Error reading the questionnaire as the editor closed:", error)
      return
    }
    const snapshot = {
      html,
      collaborative: isCollaborationReady && collaborationEnabled,
      variants,
      currentVariant,
      fullQuestionnaire,
      fetchLatestQuestionnaire,
      convertToTextFormat,
      saveQuestionnaire,
      collaborationService,
    }
    // The saves still running go first, so this checkpoint lands after them
    const inFlight = Array.from(savesInFlightRef.current)
    void (async (): Promise<void> => {
      try {
        await Promise.all(inFlight)
        if (snapshot.collaborative) {
          const latest = snapshot.fetchLatestQuestionnaire
            ? await snapshot.fetchLatestQuestionnaire()
            : snapshot.fullQuestionnaire
          const text = snapshot.convertToTextFormat(
            htmlToFilteredText(snapshot.html),
            snapshot.variants,
            snapshot.currentVariant,
            latest,
          )
          await snapshot.saveQuestionnaire(undefined, text, { checkpoint: true })
          snapshot.collaborationService?.recordSaveTimestamp(
            snapshot.currentVariant === "" ? "master" : snapshot.currentVariant,
          )
        } else {
          await snapshot.saveQuestionnaire(undefined, undefined, {
            checkpoint: true,
            editorHTML: snapshot.html,
          })
        }
      } catch (error) {
        console.error("Error saving questionnaire as the editor closed:", error)
      }
    })()
  }
  useEffect(() => () => closeRef.current(), [])

  return {
    isSaving,
    performSave,
    manualSaveQuestionnaire,
    runExclusiveSave,
  }
}
